import { useState } from "react";
import { LifeBuoy, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TOPICS = ["General question", "Order issue", "Payment problem", "Menu / allergy", "Website bug", "Other"];

export default function Contact() {
  const { user } = useAuth();
  const [subject, setSubject] = useState(TOPICS[0]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please write a message first.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { subject, message: message.trim() });
      toast.success("Message sent! We'll get back to you soon.");
      setMessage("");
      setSubject(TOPICS[0]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="contact-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="inline-flex items-center justify-center h-11 w-11 rounded-2xl bg-emerald-100 text-emerald-600">
          <LifeBuoy size={22} />
        </span>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800">Contact us</h1>
      </div>
      <p className="text-slate-500 mb-6">
        Have a question or ran into an issue? Send us a message and the Lunch by WAJ team will reply to your email.
      </p>

      <form onSubmit={submit} className="bg-white rounded-3xl border-2 border-emerald-100 p-6 sm:p-8 space-y-5">
        <div>
          <label className="block text-sm font-semibold text-slate-600 mb-2">Your details</label>
          <div
            data-testid="contact-user-info"
            className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600"
          >
            <span className="font-semibold text-slate-800">{user?.name}</span> · {user?.email}
          </div>
        </div>

        <div>
          <label htmlFor="topic" className="block text-sm font-semibold text-slate-600 mb-2">
            Topic
          </label>
          <select
            id="topic"
            data-testid="contact-subject-select"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-slate-700 focus:border-emerald-400 focus:outline-none transition-colors"
          >
            {TOPICS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-slate-600 mb-2">
            Message
          </label>
          <textarea
            id="message"
            data-testid="contact-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="Tell us what's going on..."
            className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-slate-700 focus:border-emerald-400 focus:outline-none transition-colors resize-none"
          />
        </div>

        <button
          type="submit"
          data-testid="contact-submit-button"
          disabled={sending}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold py-3.5 shadow-md shadow-emerald-200 transition-all active:scale-[0.99]"
        >
          <Send size={18} />
          {sending ? "Sending..." : "Send message"}
        </button>
      </form>
    </div>
  );
}
